import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { ApiError, api } from '../../api/index.js';

const SAN_DNS_RE = /^[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
const SAN_URI_RE = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/.+$/;

const DEFAULT = {
  nodeId: '',
  subjectCn: '',
  validityDays: 30,
  sanDns: '',
  sanUri: '',
  notes: '',
};

/**
 * Issue a new node certificate. Operating modes:
 *
 *   mode="issue"               admin picks a node, fills in metadata
 *   mode="issue-for-node"      node is pre-selected (locked), CN pre-filled from node.name
 *   mode="renew"               renews an existing cert: pre-fills everything, but the
 *                              actual server call is api.certificates.renew(certId, ...)
 *
 * The bundle (cert+key+ca) is shown by IssuedBundleModal afterwards.
 */
export function IssueCertModal({ open, mode = 'issue', node, certificate, onClose, onSubmit }) {
  const [values, setValues] = useState(DEFAULT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [loadingNodes, setLoadingNodes] = useState(false);

  const isRenew = mode === 'renew';
  const isLockedNode = mode === 'issue-for-node' || isRenew;

  // Load nodes list when needed (free-pick mode).
  useEffect(() => {
    if (!open || isLockedNode) return undefined;
    setLoadingNodes(true);
    api.nodes
      .list()
      .then((res) => setNodes(res.nodes || []))
      .catch(() => setNodes([]))
      .finally(() => setLoadingNodes(false));
    return undefined;
  }, [open, isLockedNode]);

  // Seed form values per mode.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    if (isRenew && certificate) {
      setValues({
        nodeId: certificate.node?.id || '',
        subjectCn: certificate.subjectCn || '',
        validityDays: 30,
        sanDns: (certificate.sanDns || []).join(' '),
        sanUri: (certificate.sanUri || []).join(' '),
        notes: certificate.notes || '',
      });
    } else if (mode === 'issue-for-node' && node) {
      setValues({
        ...DEFAULT,
        nodeId: node.id,
        subjectCn: node.name,
        sanDns: node.url ? hostnameFromUrl(node.url) : '',
      });
    } else {
      setValues({ ...DEFAULT });
    }
  }, [open, mode, isRenew, certificate, node]);

  const dnsList = useMemo(
    () => values.sanDns.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
    [values.sanDns],
  );
  const uriList = useMemo(
    () => values.sanUri.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
    [values.sanUri],
  );

  const errors = useMemo(() => {
    const e = {};
    if (!isRenew) {
      if (!values.nodeId) e.nodeId = 'Select a node.';
      if (!values.subjectCn.trim()) e.subjectCn = 'Subject CN is required.';
      else if (values.subjectCn.length > 128) e.subjectCn = 'Max 128 characters.';
    }
    const days = Number(values.validityDays);
    if (!Number.isFinite(days) || days <= 0 || days > 3650) {
      e.validityDays = 'Validity must be between 1 and 3650 days.';
    }
    if (!isRenew) {
      for (const d of dnsList) {
        if (!SAN_DNS_RE.test(d)) {
          e.sanDns = `"${d}" is not a valid hostname.`;
          break;
        }
      }
      for (const u of uriList) {
        if (!SAN_URI_RE.test(u)) {
          e.sanUri = `"${u}" is not a valid absolute URI.`;
          break;
        }
      }
    }
    return e;
  }, [values, dnsList, uriList, isRenew]);

  const canSubmit = Object.keys(errors).length === 0 && !busy;

  function update(field, v) {
    setValues((prev) => ({ ...prev, [field]: v }));
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const payload = isRenew
        ? {
            validityDays: Number(values.validityDays),
            notes: values.notes.trim() || undefined,
          }
        : {
            nodeId: Number(values.nodeId),
            subjectCn: values.subjectCn.trim(),
            validityDays: Number(values.validityDays),
            sanDns: dnsList,
            sanUri: uriList,
            notes: values.notes.trim() || undefined,
          };
      await onSubmit(payload);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Operation failed.');
      setBusy(false);
    }
  }

  const title = isRenew
    ? `Renew certificate — ${certificate?.subjectCn || ''}`
    : mode === 'issue-for-node'
      ? `Issue certificate for ${node?.name || ''}`
      : 'Issue node certificate';

  const description = isRenew
    ? 'Issues a new certificate with the same identity and a fresh validity period. The previous certificate is revoked with reason "superseded".'
    : 'Server generates an Ed25519 keypair and signs the certificate with the TMS root CA. The private key is returned once and never stored.';

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      description={description}
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Generating…
              </>
            ) : (
              <>
                <i className={`fa-solid ${isRenew ? 'fa-rotate-right' : 'fa-shield-halved'}`} aria-hidden="true" />
                {isRenew ? 'Renew certificate' : 'Issue certificate'}
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        {/* Node selector */}
        <div>
          <label htmlFor="node-id" className="field-label">Node</label>
          {isLockedNode ? (
            <div className="flex items-center gap-3 rounded-2xl border border-line bg-canvas px-4 py-3 text-sm">
              <i className="fa-solid fa-server text-accent" aria-hidden="true" />
              <span className="font-mono font-semibold text-ink">
                {node?.name || certificate?.node?.name || '—'}
              </span>
              <span className="rounded-full border border-line bg-white px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-mist">
                {node?.tier || certificate?.node?.tier || ''}
              </span>
              <span className="ml-auto text-[11px] text-mist">locked</span>
            </div>
          ) : (
            <>
              <select
                id="node-id"
                className="field"
                value={values.nodeId}
                onChange={(e) => {
                  const id = e.target.value;
                  const n = nodes.find((x) => String(x.id) === String(id));
                  setValues((prev) => ({
                    ...prev,
                    nodeId: id,
                    subjectCn: prev.subjectCn || n?.name || '',
                    sanDns:
                      prev.sanDns || (n?.url ? hostnameFromUrl(n.url) : ''),
                  }));
                }}
                disabled={busy || loadingNodes}
              >
                <option value="">— select node —</option>
                {nodes.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name} · {n.tier} · {n.status}
                  </option>
                ))}
              </select>
              {errors.nodeId && <p className="mt-1 text-xs text-rose-600">{errors.nodeId}</p>}
              {!loadingNodes && nodes.length === 0 && (
                <p className="mt-1 text-xs text-warning">
                  <i className="fa-solid fa-triangle-exclamation mr-1" aria-hidden="true" />
                  No nodes registered. Create one in the Nodes page first.
                </p>
              )}
            </>
          )}
        </div>

        {/* Identity (locked in renew) */}
        {!isRenew && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <label htmlFor="subject-cn" className="field-label">Subject CN</label>
                <input
                  id="subject-cn"
                  className="field font-mono text-[13px]"
                  placeholder="e.g. c2-bratislava"
                  value={values.subjectCn}
                  onChange={(e) => update('subjectCn', e.target.value)}
                  autoComplete="off"
                  spellCheck="false"
                  disabled={busy}
                />
                {errors.subjectCn && <p className="mt-1 text-xs text-rose-600">{errors.subjectCn}</p>}
              </div>
              <div>
                <label htmlFor="validity" className="field-label">Validity (days)</label>
                <input
                  id="validity"
                  type="number"
                  min="1"
                  max="3650"
                  className="field"
                  value={values.validityDays}
                  onChange={(e) => update('validityDays', e.target.value)}
                  disabled={busy}
                />
                {errors.validityDays && <p className="mt-1 text-xs text-rose-600">{errors.validityDays}</p>}
              </div>
            </div>

            <div>
              <label htmlFor="san-dns" className="field-label">
                SAN DNS <span className="font-normal text-mist">(space- or comma-separated, optional)</span>
              </label>
              <input
                id="san-dns"
                className="field font-mono text-[13px]"
                placeholder="c2-brat.example.mil  c2-brat.alt.mil"
                value={values.sanDns}
                onChange={(e) => update('sanDns', e.target.value)}
                autoComplete="off"
                spellCheck="false"
                disabled={busy}
              />
              {errors.sanDns && <p className="mt-1 text-xs text-rose-600">{errors.sanDns}</p>}
            </div>

            <div>
              <label htmlFor="san-uri" className="field-label">
                SAN URI <span className="font-normal text-mist">(absolute URIs, optional)</span>
              </label>
              <input
                id="san-uri"
                className="field font-mono text-[13px]"
                placeholder="tms-node://550e8400-e29b-41d4-a716-446655440000"
                value={values.sanUri}
                onChange={(e) => update('sanUri', e.target.value)}
                autoComplete="off"
                spellCheck="false"
                disabled={busy}
              />
              {errors.sanUri && <p className="mt-1 text-xs text-rose-600">{errors.sanUri}</p>}
            </div>
          </>
        )}

        {/* Renew = only validity is editable */}
        {isRenew && (
          <div>
            <label htmlFor="renew-validity" className="field-label">New validity (days)</label>
            <input
              id="renew-validity"
              type="number"
              min="1"
              max="3650"
              className="field"
              value={values.validityDays}
              onChange={(e) => update('validityDays', e.target.value)}
              disabled={busy}
            />
            {errors.validityDays && <p className="mt-1 text-xs text-rose-600">{errors.validityDays}</p>}
            <p className="mt-1 text-xs text-mist">
              Subject CN, SAN DNS, and SAN URI are inherited from the previous certificate and cannot
              be changed during renewal.
            </p>
          </div>
        )}

        <div>
          <label htmlFor="notes" className="field-label">
            Notes <span className="font-normal text-mist">(internal, optional)</span>
          </label>
          <textarea
            id="notes"
            className="field min-h-[70px] resize-y"
            value={values.notes}
            onChange={(e) => update('notes', e.target.value)}
            maxLength={1024}
            disabled={busy}
          />
        </div>

        <div className="rounded-2xl border border-warning/30 bg-warning-soft px-4 py-3 text-xs text-warning">
          <i className="fa-solid fa-circle-info mr-2" aria-hidden="true" />
          The private key is shown only once — make sure you save it before closing the result window.
        </div>
      </form>
    </Modal>
  );
}

function hostnameFromUrl(url) {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}
